import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibField, KibTextarea } from '@chewy/kib-fields-new-react'
import { NumberUtils, PhoneUtils } from '@pbt/pbt-ui-components'

import { getInvoiceDueToPayNoFee } from '~/components/dashboard/invoices/invoiceUtils'
import InvoiceType from '~/constants/InvoiceType'
import { fetchClient } from '~/store/actions/clients'
import { getCurrentBusiness } from '~/store/reducers/auth'
import { getPatient } from '~/store/reducers/patients'
import { getTimetableEventsMap } from '~/store/reducers/timetable'
import { getUser } from '~/store/reducers/users'

import { usePaymentRequestDialog } from '../context/PaymentRequestDialogProvider'
import {
  buildPaymentRequestV2Templates,
  getPaymentRequestVisitFromTimetable,
  getPrimaryPatientIdForPaymentRequest,
  type PaymentRequestV2TemplateInterpolation,
} from '../utils/paymentRequestDialogV2Utils'

import '@chewy/kib-fields-new-styles/dist/styles/kib-field.css'
import styles from './MessageBodySection.module.scss'

export const MessageBodySection = () => {
  const { t } = useTranslation(['Common', 'Dialogs'])
  const messageTextareaRef = useRef<HTMLTextAreaElement>(null)

  const dispatch = useDispatch()
  const { invoice, dialogState } = usePaymentRequestDialog()
  const { deliveryMethod } = dialogState

  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')

  const { clientId } = invoice
  const client = useSelector(getUser(clientId))
  const business = useSelector(getCurrentBusiness)

  const timetableEventsMap = useSelector(getTimetableEventsMap)

  const { dateDisplay: dateOfVisitFormatted, patientIdForVisit } =
    getPaymentRequestVisitFromTimetable(invoice, timetableEventsMap)
  const patientId =
    patientIdForVisit ?? getPrimaryPatientIdForPaymentRequest(invoice)
  const patient = useSelector(getPatient(patientId))

  useEffect(() => {
    if (clientId && !client) {
      dispatch(fetchClient({ clientId }))
    }
  }, [clientId, client, dispatch])

  const dueToPayNoFee = getInvoiceDueToPayNoFee(invoice)

  const templateBase = useMemo(
    () => ({
      clientFirstName: client?.firstName ?? '',
      clinicName: business?.name ?? '',
      invoiceAmount: NumberUtils.formatMoney(dueToPayNoFee),
      petName: patient?.name ?? '',
    }),
    [
      business?.name,
      client?.firstName,
      dueToPayNoFee,
      patient?.name,
      patientId,
    ],
  )

  useEffect(() => {
    const rawPhone = business?.phone ?? ''
    const clinicPhone = rawPhone
      ? (PhoneUtils.formatPhoneNumber(rawPhone) ?? rawPhone)
      : ''

    const visitPhrase =
      invoice.type !== InvoiceType.ESTIMATE && dateOfVisitFormatted
        ? t('Dialogs:PAYMENT_REQUEST_DIALOG_V2.MESSAGE.VISIT_ON_DATE', {
            dateOfVisit: dateOfVisitFormatted,
          })
        : ''

    const { subject: nextSubject, message: nextMessage } =
      buildPaymentRequestV2Templates(t, deliveryMethod, invoice, {
        ...templateBase,
        clinicPhone,
        visitPhrase,
      } as PaymentRequestV2TemplateInterpolation)

    setSubject(nextSubject)
    setMessage(nextMessage)
  }, [
    business?.phone,
    dateOfVisitFormatted,
    deliveryMethod,
    invoice.id,
    invoice.type,
    t,
    templateBase,
  ])

  useLayoutEffect(() => {
    const el = messageTextareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [deliveryMethod, message])

  return (
    <section className={styles.messageBodyContainer}>
      {deliveryMethod === 'email' ? (
        <div className={styles.readOnlyPlain}>
          <KibField
            readOnly
            label={t('Dialogs:PAYMENT_REQUEST_DIALOG_V2.SUBJECT_LABEL')}
            value={subject}
          />
        </div>
      ) : null}
      <div className={styles.readOnlyPlain}>
        <KibTextarea
          readOnly
          label={t('Dialogs:PAYMENT_REQUEST_DIALOG_V2.MESSAGE.MESSAGE_LABEL')}
          ref={messageTextareaRef}
          rows={1}
          value={message}
        />
        {deliveryMethod === 'email' ? (
          <div className={styles.reviewAndPayCta}>
            {invoice.type === InvoiceType.ESTIMATE
              ? t(
                  'Dialogs:PAYMENT_REQUEST_DIALOG_V2.MESSAGE.REVIEW_ESTIMATE_AND_PAY_DEPOSIT',
                )
              : t('Dialogs:PAYMENT_REQUEST_DIALOG_V2.MESSAGE.REVIEW_AND_PAY')}
          </div>
        ) : null}
      </div>
    </section>
  )
}
